import { useState, useEffect, useCallback } from "react";
import { isSyncEnabled } from "./useProgressSync";
import { FRUITS } from "./useGameStateV2";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// ============== FRUIT LEVEL SYSTEM ==============
// Each fruit has its own level (1-100) based on eliminations
// Level 2 requires 100 eliminations, then 1.038x factor per level
// Total ~100,000 eliminations to reach max level 100

const FRUIT_LEVEL_BASE = 100; // Eliminations needed for level 2
const FRUIT_LEVEL_FACTOR = 1.038; // Multiplier per level (~100K total for level 100)
const MAX_FRUIT_LEVEL = 100;

// Calculate total eliminations needed to reach a specific fruit level
export const getTotalEliminationsForFruitLevel = (level) => {
  if (level <= 1) return 0;
  let total = 0;
  for (let i = 2; i <= Math.min(level, MAX_FRUIT_LEVEL); i++) {
    total += Math.floor(FRUIT_LEVEL_BASE * Math.pow(FRUIT_LEVEL_FACTOR, i - 2));
  }
  return total;
};

// Calculate eliminations needed for next fruit level
export const getEliminationsForNextFruitLevel = (currentLevel) => {
  if (currentLevel >= MAX_FRUIT_LEVEL) return Infinity;
  return Math.floor(FRUIT_LEVEL_BASE * Math.pow(FRUIT_LEVEL_FACTOR, currentLevel - 1));
};

// Calculate fruit level from total eliminations
export const getFruitLevelFromEliminations = (eliminations) => {
  if (eliminations < FRUIT_LEVEL_BASE) return 1;
  
  let level = 1;
  let totalNeeded = 0;
  
  while (level < MAX_FRUIT_LEVEL) {
    const neededForNext = Math.floor(FRUIT_LEVEL_BASE * Math.pow(FRUIT_LEVEL_FACTOR, level - 1));
    if (totalNeeded + neededForNext > eliminations) {
      break;
    }
    totalNeeded += neededForNext;
    level++;
  }
  
  return Math.min(level, MAX_FRUIT_LEVEL);
};

// Get fruit level progress (current XP within level, needed for next, percentage)
export const getFruitLevelProgress = (eliminations) => {
  const level = getFruitLevelFromEliminations(eliminations);
  const eliminationsAtCurrentLevel = getTotalEliminationsForFruitLevel(level);
  const eliminationsForNextLevel = getEliminationsForNextFruitLevel(level);
  const eliminationsInCurrentLevel = eliminations - eliminationsAtCurrentLevel;
  
  if (level >= MAX_FRUIT_LEVEL) {
    return {
      level,
      current: eliminationsInCurrentLevel,
      needed: 0,
      percentage: 100,
      total: eliminations,
      isMaxLevel: true,
    };
  }
  
  return {
    level,
    current: eliminationsInCurrentLevel,
    needed: eliminationsForNextLevel,
    percentage: Math.min((eliminationsInCurrentLevel / eliminationsForNextLevel) * 100, 100),
    total: eliminations,
    isMaxLevel: false,
  };
};

// Get all fruit types for the level display
export const ALL_FRUIT_TYPES = [
  { id: 'banana', name: 'Banana', emoji: '🍌' },
  { id: 'apple', name: 'Apple', emoji: '🍎' },
  { id: 'pear', name: 'Pear', emoji: '🍐' },
  { id: 'pineapple', name: 'Pineapple', emoji: '🍍' },
  { id: 'orange', name: 'Orange', emoji: '🍊' },
  { id: 'peach', name: 'Peach', emoji: '🍑' },
  { id: 'strawberry', name: 'Strawberry', emoji: '🍓' },
  { id: 'lemon', name: 'Lemon', emoji: '🍋' },
  { id: 'cherry', name: 'Cherry', emoji: '🍒' },
  { id: 'watermelon', name: 'Watermelon', emoji: '🍉' },
  { id: 'mango', name: 'Mango', emoji: '🥭' },
  { id: 'kiwi', name: 'Kiwi', emoji: '🥝' },
  { id: 'blueberry', name: 'Blueberry', emoji: '🫐' },
];

// ============== BACKEND SYNC ==============

// Sync stats to backend
const syncStatsToBackend = async (stats, claimedChallenges) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;

  try {
    await fetch(`${API_URL}/api/progress`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ stats, claimed_challenges: claimedChallenges })
    });
  } catch (error) {
    console.error('Failed to sync stats:', error);
  }
};

// Challenge definitions
export const CHALLENGES = {
  regular: [
    // Fruit Elimination - Basic
    { id: 'r1', title: 'Fruit Beginner', description: 'Eliminate 100 fruits', target: 100, type: 'total_fruits', reward: 1000, icon: '🍎' },
    { id: 'r2', title: 'Banana Hunter', description: 'Eliminate 100 bananas', target: 100, type: 'banana', reward: 1000, icon: '🍌' },
    { id: 'r3', title: 'Apple Collector', description: 'Eliminate 100 apples', target: 100, type: 'apple', reward: 1000, icon: '🍎' },
    { id: 'r4', title: 'Peach Lover', description: 'Eliminate 50 peaches', target: 50, type: 'peach', reward: 500, icon: '🍑' },
    { id: 'r5', title: 'Fruit Crusher', description: 'Eliminate 150 fruits', target: 150, type: 'total_fruits', reward: 1500, icon: '🍇' },
    { id: 'r6', title: 'Cherry Champion', description: 'Eliminate 200 cherries', target: 200, type: 'cherry', reward: 2000, icon: '🍒' },
    { id: 'r7', title: 'Watermelon Warrior', description: 'Eliminate 200 watermelons', target: 200, type: 'watermelon', reward: 3000, icon: '🍉' },
    { id: 'r8', title: 'Fruit Expert', description: 'Eliminate 500 fruits', target: 500, type: 'total_fruits', reward: 5000, icon: '🏆' },
    { id: 'r9', title: 'Fruit Master', description: 'Eliminate 1,000 fruits', target: 1000, type: 'total_fruits', reward: 10000, icon: '👑' },
    { id: 'r10', title: 'Fruit Legend', description: 'Eliminate 2,000 fruits', target: 2000, type: 'total_fruits', reward: 20000, icon: '🌟' },
    
    // Specific Fruit Challenges - Tier 1
    { id: 'r11', title: 'Pear Picker', description: 'Eliminate 75 pears', target: 75, type: 'pear', reward: 750, icon: '🍐' },
    { id: 'r12', title: 'Pineapple Party', description: 'Eliminate 100 pineapples', target: 100, type: 'pineapple', reward: 1000, icon: '🍍' },
    { id: 'r13', title: 'Orange Obsession', description: 'Eliminate 100 oranges', target: 100, type: 'orange', reward: 1000, icon: '🍊' },
    { id: 'r14', title: 'Strawberry Sweetheart', description: 'Eliminate 150 strawberries', target: 150, type: 'strawberry', reward: 1500, icon: '🍓' },
    { id: 'r15', title: 'Lemon Lover', description: 'Eliminate 100 lemons', target: 100, type: 'lemon', reward: 1000, icon: '🍋' },
    { id: 'r16', title: 'Mango Mania', description: 'Eliminate 150 mangos', target: 150, type: 'mango', reward: 2000, icon: '🥭' },
    { id: 'r17', title: 'Kiwi Krusher', description: 'Eliminate 100 kiwis', target: 100, type: 'kiwi', reward: 1500, icon: '🥝' },
    { id: 'r18', title: 'Blueberry Blitz', description: 'Eliminate 150 blueberries', target: 150, type: 'blueberry', reward: 2000, icon: '🫐' },
    
    // Specific Fruit Challenges - Tier 2
    { id: 'r19', title: 'Banana Baron', description: 'Eliminate 300 bananas', target: 300, type: 'banana', reward: 3000, icon: '🍌' },
    { id: 'r20', title: 'Apple Addict', description: 'Eliminate 300 apples', target: 300, type: 'apple', reward: 3000, icon: '🍎' },
    { id: 'r21', title: 'Peachy Keen', description: 'Eliminate 200 peaches', target: 200, type: 'peach', reward: 2500, icon: '🍑' },
    { id: 'r22', title: 'Cherry Crusher', description: 'Eliminate 400 cherries', target: 400, type: 'cherry', reward: 4000, icon: '🍒' },
    { id: 'r23', title: 'Melon Master', description: 'Eliminate 350 watermelons', target: 350, type: 'watermelon', reward: 4500, icon: '🍉' },
    { id: 'r24', title: 'Pear Pro', description: 'Eliminate 250 pears', target: 250, type: 'pear', reward: 2500, icon: '🍐' },
    { id: 'r25', title: 'Tropical Titan', description: 'Eliminate 300 pineapples', target: 300, type: 'pineapple', reward: 3500, icon: '🍍' },
    { id: 'r26', title: 'Citrus King', description: 'Eliminate 350 oranges', target: 350, type: 'orange', reward: 3500, icon: '🍊' },
    
    // Total Fruit Milestones
    { id: 'r27', title: 'Fruit Fanatic', description: 'Eliminate 3,000 fruits', target: 3000, type: 'total_fruits', reward: 30000, icon: '🎯' },
    { id: 'r28', title: 'Fruit Champion', description: 'Eliminate 4,000 fruits', target: 4000, type: 'total_fruits', reward: 40000, icon: '🏅' },
    { id: 'r29', title: 'Fruit Hero', description: 'Eliminate 5,000 fruits', target: 5000, type: 'total_fruits', reward: 50000, icon: '🦸' },
    { id: 'r30', title: 'Fruit Conqueror', description: 'Eliminate 6,000 fruits', target: 6000, type: 'total_fruits', reward: 60000, icon: '⚔️' },
    
    // Specific Fruit Challenges - Tier 3
    { id: 'r31', title: 'Strawberry Storm', description: 'Eliminate 400 strawberries', target: 400, type: 'strawberry', reward: 5000, icon: '🍓' },
    { id: 'r32', title: 'Lemon Legend', description: 'Eliminate 350 lemons', target: 350, type: 'lemon', reward: 4000, icon: '🍋' },
    { id: 'r33', title: 'Mango Madness', description: 'Eliminate 400 mangos', target: 400, type: 'mango', reward: 5500, icon: '🥭' },
    { id: 'r34', title: 'Kiwi King', description: 'Eliminate 300 kiwis', target: 300, type: 'kiwi', reward: 4000, icon: '🥝' },
    { id: 'r35', title: 'Berry Boss', description: 'Eliminate 400 blueberries', target: 400, type: 'blueberry', reward: 5500, icon: '🫐' },
    
    // Disco Combo Challenges
    { id: 'r36', title: 'Disco Starter', description: 'Create 10 disco combos', target: 10, type: 'disco_combos', reward: 1500, icon: '🪩' },
    { id: 'r37', title: 'Disco Dancer', description: 'Create 25 disco combos', target: 25, type: 'disco_combos', reward: 3000, icon: '💃' },
    { id: 'r38', title: 'Disco Fever', description: 'Create 50 disco combos', target: 50, type: 'disco_combos', reward: 6000, icon: '🕺' },
    
    // More Total Fruit Milestones
    { id: 'r39', title: 'Fruit Destroyer', description: 'Eliminate 7,500 fruits', target: 7500, type: 'total_fruits', reward: 75000, icon: '💥' },
    { id: 'r40', title: 'Fruit Overlord', description: 'Eliminate 8,500 fruits', target: 8500, type: 'total_fruits', reward: 85000, icon: '👹' },
    
    // Tier 4 Specific Fruits
    { id: 'r41', title: 'Banana Boss', description: 'Eliminate 500 bananas', target: 500, type: 'banana', reward: 6000, icon: '🍌' },
    { id: 'r42', title: 'Apple Ace', description: 'Eliminate 500 apples', target: 500, type: 'apple', reward: 6000, icon: '🍎' },
    { id: 'r43', title: 'Cherry Chief', description: 'Eliminate 600 cherries', target: 600, type: 'cherry', reward: 7500, icon: '🍒' },
    { id: 'r44', title: 'Melon Monarch', description: 'Eliminate 500 watermelons', target: 500, type: 'watermelon', reward: 7000, icon: '🍉' },
    { id: 'r45', title: 'Citrus Champion', description: 'Eliminate 500 oranges', target: 500, type: 'orange', reward: 6000, icon: '🍊' },
    { id: 'r46', title: 'Tropical Thunder', description: 'Eliminate 450 pineapples', target: 450, type: 'pineapple', reward: 5500, icon: '🍍' },
    { id: 'r47', title: 'Peachy Perfect', description: 'Eliminate 400 peaches', target: 400, type: 'peach', reward: 5000, icon: '🍑' },
    { id: 'r48', title: 'Pear Paradise', description: 'Eliminate 400 pears', target: 400, type: 'pear', reward: 5000, icon: '🍐' },
    { id: 'r49', title: 'Strawberry Superstar', description: 'Eliminate 600 strawberries', target: 600, type: 'strawberry', reward: 8000, icon: '🍓' },
    { id: 'r50', title: 'Lemon Lightning', description: 'Eliminate 500 lemons', target: 500, type: 'lemon', reward: 6500, icon: '🍋' },
    
    // ============== NEW REGULAR CHALLENGES (51-100) ==============
    
    // Item Collection Challenges
    { id: 'r51', title: 'Star Collector', description: 'Earn 50 stars from levels', target: 50, type: 'stars_earned', reward: 5000, icon: '⭐' },
    { id: 'r52', title: 'Star Hunter', description: 'Earn 100 stars from levels', target: 100, type: 'stars_earned', reward: 12000, icon: '🌟' },
    { id: 'r53', title: 'Star Master', description: 'Earn 200 stars from levels', target: 200, type: 'stars_earned', reward: 25000, icon: '✨' },
    { id: 'r54', title: 'Coin Beginner', description: 'Collect 1,000 coins', target: 1000, type: 'coins_earned', reward: 2000, icon: '🪙' },
    { id: 'r55', title: 'Coin Collector', description: 'Collect 5,000 coins', target: 5000, type: 'coins_earned', reward: 8000, icon: '💰' },
    { id: 'r56', title: 'Coin Hoarder', description: 'Collect 15,000 coins', target: 15000, type: 'coins_earned', reward: 20000, icon: '🏦' },
    { id: 'r57', title: 'Power-Up Newbie', description: 'Use 10 power-ups', target: 10, type: 'powerups_used', reward: 1500, icon: '🔮' },
    { id: 'r58', title: 'Power-Up Fan', description: 'Use 50 power-ups', target: 50, type: 'powerups_used', reward: 6000, icon: '⚡' },
    { id: 'r59', title: 'Power-Up Addict', description: 'Use 150 power-ups', target: 150, type: 'powerups_used', reward: 15000, icon: '💎' },
    
    // Level Completion Challenges
    { id: 'r60', title: 'First Steps', description: 'Complete 5 levels', target: 5, type: 'levels_completed', reward: 1000, icon: '👣' },
    { id: 'r61', title: 'World Explorer', description: 'Complete 15 levels', target: 15, type: 'levels_completed', reward: 3000, icon: '🗺️' },
    { id: 'r62', title: 'Adventurer', description: 'Complete 30 levels', target: 30, type: 'levels_completed', reward: 7500, icon: '🧭' },
    { id: 'r63', title: 'World Traveler', description: 'Complete 50 levels', target: 50, type: 'levels_completed', reward: 15000, icon: '🌍' },
    { id: 'r64', title: 'Globe Trotter', description: 'Complete 75 levels', target: 75, type: 'levels_completed', reward: 25000, icon: '✈️' },
    { id: 'r65', title: 'World Conqueror', description: 'Complete 100 levels', target: 100, type: 'levels_completed', reward: 50000, icon: '👑' },
    
    // Perfect Level Challenges
    { id: 'r66', title: 'Flawless Start', description: 'Complete a level with 3 stars', target: 1, type: 'three_star_levels', reward: 500, icon: '🌟' },
    { id: 'r67', title: 'Perfectionist', description: 'Get 3 stars on 10 levels', target: 10, type: 'three_star_levels', reward: 5000, icon: '💯' },
    { id: 'r68', title: 'Star Achiever', description: 'Get 3 stars on 25 levels', target: 25, type: 'three_star_levels', reward: 12000, icon: '🏅' },
    { id: 'r69', title: 'Gold Standard', description: 'Get 3 stars on 50 levels', target: 50, type: 'three_star_levels', reward: 30000, icon: '🥇' },
    
    // Cascade/Combo Challenges
    { id: 'r70', title: 'Chain Reaction', description: 'Trigger 50 cascades', target: 50, type: 'cascades', reward: 2500, icon: '⛓️' },
    { id: 'r71', title: 'Combo Starter', description: 'Trigger 150 cascades', target: 150, type: 'cascades', reward: 7500, icon: '🔗' },
    { id: 'r72', title: 'Cascade King', description: 'Trigger 400 cascades', target: 400, type: 'cascades', reward: 20000, icon: '🌊' },
    { id: 'r73', title: 'Chain Master', description: 'Get a 5+ chain combo', target: 1, type: 'max_chain', reward: 3000, icon: '🔥' },
    { id: 'r74', title: 'Mega Chain', description: 'Get a 7+ chain combo', target: 1, type: 'max_chain', reward: 8000, icon: '💥' },
    
    // Special Fruit Creation
    { id: 'r75', title: 'Stripe Rookie', description: 'Create 25 striped fruits', target: 25, type: 'striped_fruits', reward: 2000, icon: '➖' },
    { id: 'r76', title: 'Stripe Expert', description: 'Create 100 striped fruits', target: 100, type: 'striped_fruits', reward: 10000, icon: '📏' },
    { id: 'r77', title: 'Wrap Rookie', description: 'Create 25 wrapped fruits', target: 25, type: 'wrapped_fruits', reward: 3000, icon: '🎁' },
    { id: 'r78', title: 'Wrap Expert', description: 'Create 100 wrapped fruits', target: 100, type: 'wrapped_fruits', reward: 15000, icon: '📦' },
    
    // Score Challenges
    { id: 'r79', title: 'Score Chaser', description: 'Score 50,000 points in a level', target: 50000, type: 'level_high_score', reward: 3000, icon: '🎯' },
    { id: 'r80', title: 'Score Hunter', description: 'Score 100,000 points in a level', target: 100000, type: 'level_high_score', reward: 8000, icon: '🏹' },
    { id: 'r81', title: 'Score Champion', description: 'Score 200,000 points in a level', target: 200000, type: 'level_high_score', reward: 20000, icon: '🏆' },
    
    // Move Efficiency Challenges
    { id: 'r82', title: 'Efficient Player', description: 'Complete a level with 5+ moves remaining', target: 1, type: 'moves_remaining', reward: 2000, icon: '♻️' },
    { id: 'r83', title: 'Move Saver', description: 'Complete 10 levels with moves remaining', target: 10, type: 'efficient_clears', reward: 7500, icon: '💡' },
    { id: 'r84', title: 'Strategic Mind', description: 'Complete 25 levels with moves remaining', target: 25, type: 'efficient_clears', reward: 15000, icon: '🧠' },
    
    // Daily Play Challenges
    { id: 'r85', title: 'Daily Player', description: 'Play 7 days in a row', target: 7, type: 'login_streak', reward: 5000, icon: '📅' },
    { id: 'r86', title: 'Dedicated Player', description: 'Play 14 days in a row', target: 14, type: 'login_streak', reward: 12000, icon: '📆' },
    { id: 'r87', title: 'Loyal Player', description: 'Play 30 days in a row', target: 30, type: 'login_streak', reward: 30000, icon: '🗓️' },
    
    // Obstacle Challenges
    { id: 'r88', title: 'Ice Breaker', description: 'Break 100 ice blocks', target: 100, type: 'ice_broken', reward: 3000, icon: '🧊' },
    { id: 'r89', title: 'Ice Crusher', description: 'Break 500 ice blocks', target: 500, type: 'ice_broken', reward: 12000, icon: '❄️' },
    { id: 'r90', title: 'Chain Breaker', description: 'Break 100 chains', target: 100, type: 'chains_broken', reward: 3500, icon: '⛓️' },
    { id: 'r91', title: 'Chain Master', description: 'Break 500 chains', target: 500, type: 'chains_broken', reward: 14000, icon: '🔓' },
    { id: 'r92', title: 'Bubble Popper', description: 'Pop 100 bubbles', target: 100, type: 'bubbles_popped', reward: 4000, icon: '🫧' },
    { id: 'r93', title: 'Bubble Buster', description: 'Pop 500 bubbles', target: 500, type: 'bubbles_popped', reward: 16000, icon: '💨' },
    
    // Boss Challenges
    { id: 'r94', title: 'Boss Challenger', description: 'Attempt 5 boss levels', target: 5, type: 'boss_attempts', reward: 5000, icon: '👹' },
    { id: 'r95', title: 'Boss Hunter', description: 'Defeat 3 bosses', target: 3, type: 'bosses_defeated', reward: 15000, icon: '🗡️' },
    { id: 'r96', title: 'Boss Slayer', description: 'Defeat 10 bosses', target: 10, type: 'bosses_defeated', reward: 40000, icon: '⚔️' },
    { id: 'r97', title: 'Boss Dominator', description: 'Defeat 25 bosses', target: 25, type: 'bosses_defeated', reward: 100000, icon: '🏴' },
    
    // Speed Challenges
    { id: 'r98', title: 'Speed Runner', description: 'Complete a level in under 60 seconds', target: 1, type: 'speed_clear', reward: 3000, icon: '⏱️' },
    { id: 'r99', title: 'Lightning Fast', description: 'Complete 5 levels in under 60 seconds', target: 5, type: 'speed_clears', reward: 10000, icon: '⚡' },
    { id: 'r100', title: 'Time Master', description: 'Complete 15 levels in under 60 seconds', target: 15, type: 'speed_clears', reward: 30000, icon: '🚀' },
  ],
  master: [
    // Ultimate Fruit Challenges
    { id: 'm1', title: 'Ultimate Crusher', description: 'Eliminate 10,000 fruits', target: 10000, type: 'total_fruits', reward: 100000, icon: '💎' },
    { id: 'm2', title: 'Banana King', description: 'Eliminate 5,000 bananas', target: 5000, type: 'banana', reward: 50000, icon: '🍌' },
    { id: 'm3', title: 'Disco Master', description: 'Create 100 disco combos', target: 100, type: 'disco_combos', reward: 10000, icon: '🪩' },
    { id: 'm4', title: 'First Prestige', description: 'Reach Prestige 1', target: 1, type: 'prestige', reward: 10000, icon: '⭐' },
    { id: 'm5', title: 'Prestige Master', description: 'Reach Prestige 3', target: 3, type: 'prestige', reward: 30000, icon: '🌟' },
    
    // Mega Fruit Challenges
    { id: 'm6', title: 'Apple Emperor', description: 'Eliminate 10,000 apples', target: 10000, type: 'apple', reward: 100000, icon: '🍎' },
    { id: 'm7', title: 'Cherry Overlord', description: 'Eliminate 8,000 cherries', target: 8000, type: 'cherry', reward: 90000, icon: '🍒' },
    { id: 'm8', title: 'Watermelon Warlord', description: 'Eliminate 7,500 watermelons', target: 7500, type: 'watermelon', reward: 85000, icon: '🍉' },
    { id: 'm9', title: 'Strawberry Sovereign', description: 'Eliminate 8,500 strawberries', target: 8500, type: 'strawberry', reward: 95000, icon: '🍓' },
    { id: 'm10', title: 'Mango Mogul', description: 'Eliminate 6,000 mangos', target: 6000, type: 'mango', reward: 75000, icon: '🥭' },
    
    // Ultimate Milestones
    { id: 'm11', title: 'Fruit God', description: 'Eliminate 25,000 fruits', target: 25000, type: 'total_fruits', reward: 250000, icon: '🌈' },
    { id: 'm12', title: 'Fruit Immortal', description: 'Eliminate 50,000 fruits', target: 50000, type: 'total_fruits', reward: 500000, icon: '♾️' },
    { id: 'm13', title: 'Disco Legend', description: 'Create 500 disco combos', target: 500, type: 'disco_combos', reward: 75000, icon: '🎆' },
    { id: 'm14', title: 'Disco God', description: 'Create 1,000 disco combos', target: 1000, type: 'disco_combos', reward: 150000, icon: '✨' },
    
    // Ultimate Prestige (max is now 5)
    { id: 'm15', title: 'Prestige Legend', description: 'Reach Prestige 4', target: 4, type: 'prestige', reward: 50000, icon: '🔥' },
    { id: 'm16', title: 'Prestige God', description: 'Reach Prestige 5 (MAX)', target: 5, type: 'prestige', reward: 100000, icon: '💫' },
    
    // Ultimate Specific Fruits
    { id: 'm17', title: 'Blueberry Billionaire', description: 'Eliminate 10,000 blueberries', target: 10000, type: 'blueberry', reward: 110000, icon: '🫐' },
    { id: 'm18', title: 'Kiwi Kaiser', description: 'Eliminate 8,000 kiwis', target: 8000, type: 'kiwi', reward: 90000, icon: '🥝' },
    { id: 'm19', title: 'Lemon Lord', description: 'Eliminate 7,500 lemons', target: 7500, type: 'lemon', reward: 85000, icon: '🍋' },
    
    // NEW: Additional Challenges
    { id: 'm20', title: 'Orange Oracle', description: 'Eliminate 9,000 oranges', target: 9000, type: 'orange', reward: 95000, icon: '🍊' },
    { id: 'm21', title: 'Pineapple Prince', description: 'Eliminate 7,000 pineapples', target: 7000, type: 'pineapple', reward: 80000, icon: '🍍' },
    { id: 'm22', title: 'Peach Perfectionist', description: 'Eliminate 6,500 peaches', target: 6500, type: 'peach', reward: 75000, icon: '🍑' },
    { id: 'm23', title: 'Pear Prodigy', description: 'Eliminate 6,000 pears', target: 6000, type: 'pear', reward: 70000, icon: '🍐' },
    { id: 'm24', title: 'Disco Infinity', description: 'Create 2,500 disco combos', target: 2500, type: 'disco_combos', reward: 300000, icon: '🌀' },
    { id: 'm25', title: 'Fruit Titan', description: 'Eliminate 75,000 fruits', target: 75000, type: 'total_fruits', reward: 750000, icon: '🗿' },
    { id: 'm26', title: 'Fruit Universe', description: 'Eliminate 100,000 fruits', target: 100000, type: 'total_fruits', reward: 1000000, icon: '🌌' },
    { id: 'm27', title: 'Banana Billionaire', description: 'Eliminate 15,000 bananas', target: 15000, type: 'banana', reward: 150000, icon: '💰' },
    { id: 'm28', title: 'Rainbow Crusher', description: 'Eliminate 5,000 of each fruit type', target: 65000, type: 'total_fruits', reward: 500000, icon: '🌈' },
    { id: 'm29', title: 'Eternal Blaster', description: 'Create 5,000 disco combos', target: 5000, type: 'disco_combos', reward: 600000, icon: '💥' },
    
    // NEW: 10 Additional Challenges
    { id: 'm30', title: 'Striped Starter', description: 'Create 50 striped fruits', target: 50, type: 'striped_fruits', reward: 5000, icon: '🎯' },
    { id: 'm31', title: 'Striped Master', description: 'Create 200 striped fruits', target: 200, type: 'striped_fruits', reward: 25000, icon: '⚡' },
    { id: 'm32', title: 'Wrapped Wonder', description: 'Create 50 wrapped fruits', target: 50, type: 'wrapped_fruits', reward: 7500, icon: '🎁' },
    { id: 'm33', title: 'Wrapped Warrior', description: 'Create 200 wrapped fruits', target: 200, type: 'wrapped_fruits', reward: 35000, icon: '💣' },
    { id: 'm34', title: 'Combo King', description: 'Trigger 100 special combos', target: 100, type: 'special_combos', reward: 15000, icon: '🔥' },
    { id: 'm35', title: 'Combo Emperor', description: 'Trigger 500 special combos', target: 500, type: 'special_combos', reward: 75000, icon: '👑' },
    { id: 'm36', title: 'Level Climber', description: 'Complete 25 levels', target: 25, type: 'levels_completed', reward: 20000, icon: '🏔️' },
    { id: 'm37', title: 'Level Champion', description: 'Complete all 50 levels', target: 50, type: 'levels_completed', reward: 100000, icon: '🏆' },
    { id: 'm38', title: 'Perfect Player', description: 'Win 10 levels without losing a life', target: 10, type: 'perfect_wins', reward: 50000, icon: '✨' },
    { id: 'm39', title: 'Fruit Millionaire', description: 'Earn 1,000,000 total XP', target: 1000000, type: 'total_xp', reward: 200000, icon: '💎' },
    
    // ============== NEW SPECIAL FRUIT CHALLENGES (m40-m76) ==============
    
    // Tier 5 - Fruit Mastery
    { id: 'm40', title: 'Banana Emperor', description: 'Eliminate 25,000 bananas', target: 25000, type: 'banana', reward: 250000, icon: '🍌' },
    { id: 'm41', title: 'Apple Archmage', description: 'Eliminate 20,000 apples', target: 20000, type: 'apple', reward: 200000, icon: '🍎' },
    { id: 'm42', title: 'Cherry Celestial', description: 'Eliminate 15,000 cherries', target: 15000, type: 'cherry', reward: 175000, icon: '🍒' },
    { id: 'm43', title: 'Watermelon Wizard', description: 'Eliminate 12,000 watermelons', target: 12000, type: 'watermelon', reward: 150000, icon: '🍉' },
    { id: 'm44', title: 'Strawberry Saint', description: 'Eliminate 14,000 strawberries', target: 14000, type: 'strawberry', reward: 165000, icon: '🍓' },
    { id: 'm45', title: 'Mango Mystic', description: 'Eliminate 11,000 mangos', target: 11000, type: 'mango', reward: 140000, icon: '🥭' },
    
    // Blueberry Excellence
    { id: 'm46', title: 'Blueberry Baron', description: 'Eliminate 5,000 blueberries', target: 5000, type: 'blueberry', reward: 60000, icon: '🫐' },
    { id: 'm47', title: 'Blueberry Duke', description: 'Eliminate 15,000 blueberries', target: 15000, type: 'blueberry', reward: 175000, icon: '🫐' },
    { id: 'm48', title: 'Blueberry Deity', description: 'Eliminate 25,000 blueberries', target: 25000, type: 'blueberry', reward: 300000, icon: '🫐' },
    
    // Kiwi Excellence
    { id: 'm49', title: 'Kiwi Knight', description: 'Eliminate 5,000 kiwis', target: 5000, type: 'kiwi', reward: 55000, icon: '🥝' },
    { id: 'm50', title: 'Kiwi Conqueror', description: 'Eliminate 12,000 kiwis', target: 12000, type: 'kiwi', reward: 145000, icon: '🥝' },
    { id: 'm51', title: 'Kiwi Cosmos', description: 'Eliminate 20,000 kiwis', target: 20000, type: 'kiwi', reward: 240000, icon: '🥝' },
    
    // Lemon Excellence
    { id: 'm52', title: 'Lemon Luminary', description: 'Eliminate 5,000 lemons', target: 5000, type: 'lemon', reward: 55000, icon: '🍋' },
    { id: 'm53', title: 'Lemon Legend', description: 'Eliminate 12,000 lemons', target: 12000, type: 'lemon', reward: 140000, icon: '🍋' },
    { id: 'm54', title: 'Lemon Legacy', description: 'Eliminate 20,000 lemons', target: 20000, type: 'lemon', reward: 235000, icon: '🍋' },
    
    // Orange Excellence
    { id: 'm55', title: 'Orange Overlord', description: 'Eliminate 15,000 oranges', target: 15000, type: 'orange', reward: 170000, icon: '🍊' },
    { id: 'm56', title: 'Orange Omnipotent', description: 'Eliminate 25,000 oranges', target: 25000, type: 'orange', reward: 290000, icon: '🍊' },
    
    // Pineapple Excellence
    { id: 'm57', title: 'Pineapple Paladin', description: 'Eliminate 12,000 pineapples', target: 12000, type: 'pineapple', reward: 145000, icon: '🍍' },
    { id: 'm58', title: 'Pineapple Paragon', description: 'Eliminate 20,000 pineapples', target: 20000, type: 'pineapple', reward: 235000, icon: '🍍' },
    
    // Peach Excellence
    { id: 'm59', title: 'Peach Paladin', description: 'Eliminate 10,000 peaches', target: 10000, type: 'peach', reward: 120000, icon: '🍑' },
    { id: 'm60', title: 'Peach Phoenix', description: 'Eliminate 18,000 peaches', target: 18000, type: 'peach', reward: 210000, icon: '🍑' },
    
    // Pear Excellence
    { id: 'm61', title: 'Pear Paladin', description: 'Eliminate 10,000 pears', target: 10000, type: 'pear', reward: 115000, icon: '🍐' },
    { id: 'm62', title: 'Pear Perfection', description: 'Eliminate 16,000 pears', target: 16000, type: 'pear', reward: 185000, icon: '🍐' },
    
    // Ultimate Boss Challenges
    { id: 'm63', title: 'Kraken Slayer', description: 'Defeat the Kraken boss', target: 1, type: 'kraken_defeated', reward: 50000, icon: '🦑' },
    { id: 'm64', title: 'Frost Giant Feller', description: 'Defeat the Frost Giant boss', target: 1, type: 'frost_giant_defeated', reward: 60000, icon: '❄️' },
    { id: 'm65', title: 'Dragon Dominator', description: 'Defeat the Dragon boss', target: 1, type: 'dragon_defeated', reward: 75000, icon: '🐉' },
    { id: 'm66', title: 'Boss Annihilator', description: 'Defeat 50 bosses total', target: 50, type: 'bosses_defeated', reward: 250000, icon: '💀' },
    { id: 'm67', title: 'Boss Extinction', description: 'Defeat 100 bosses total', target: 100, type: 'bosses_defeated', reward: 500000, icon: '☠️' },
    
    // Item Collection Mastery
    { id: 'm68', title: 'Star Galaxy', description: 'Earn 500 stars', target: 500, type: 'stars_earned', reward: 100000, icon: '🌠' },
    { id: 'm69', title: 'Star Universe', description: 'Earn 1,000 stars', target: 1000, type: 'stars_earned', reward: 250000, icon: '🌌' },
    { id: 'm70', title: 'Coin Tycoon', description: 'Collect 100,000 coins', target: 100000, type: 'coins_earned', reward: 150000, icon: '🏆' },
    { id: 'm71', title: 'Coin Empire', description: 'Collect 500,000 coins', target: 500000, type: 'coins_earned', reward: 400000, icon: '👑' },
    
    // Perfect Run Challenges
    { id: 'm72', title: 'Flawless 25', description: 'Complete 25 levels without losing a life', target: 25, type: 'perfect_wins', reward: 125000, icon: '💯' },
    { id: 'm73', title: 'Flawless 50', description: 'Complete 50 levels without losing a life', target: 50, type: 'perfect_wins', reward: 300000, icon: '🎖️' },
    
    // Grand Mastery Challenges
    { id: 'm74', title: 'Fruit Infinity', description: 'Eliminate 200,000 fruits', target: 200000, type: 'total_fruits', reward: 2000000, icon: '♾️' },
    { id: 'm75', title: 'Level Grandmaster', description: 'Complete 200 levels', target: 200, type: 'levels_completed', reward: 500000, icon: '🎓' },
    { id: 'm76', title: 'XP Deity', description: 'Earn 10,000,000 total XP', target: 10000000, type: 'total_xp', reward: 1000000, icon: '🌟' },
  ],
};

// Initial stats structure
const INITIAL_STATS = {
  total_fruits: 0,
  banana: 0,
  apple: 0,
  pear: 0,
  pineapple: 0,
  orange: 0,
  peach: 0,
  strawberry: 0,
  lemon: 0,
  cherry: 0,
  watermelon: 0,
  mango: 0,
  kiwi: 0,
  blueberry: 0,
  disco_combos: 0,
  striped_fruits: 0,
  wrapped_fruits: 0,
  special_combos: 0,
  levels_completed: 0,
  perfect_wins: 0,
  total_xp: 0,
  // New stats for expanded challenges
  stars_earned: 0,
  coins_earned: 0,
  powerups_used: 0,
  three_star_levels: 0,
  cascades: 0,
  max_chain: 0,
  level_high_score: 0,
  moves_remaining: 0,
  efficient_clears: 0,
  login_streak: 0,
  ice_broken: 0,
  chains_broken: 0,
  bubbles_popped: 0,
  boss_attempts: 0,
  bosses_defeated: 0,
  speed_clear: 0,
  speed_clears: 0,
  kraken_defeated: 0,
  frost_giant_defeated: 0,
  dragon_defeated: 0,
};

// Load stats from backend (called on mount to restore after localStorage loss)
const loadStatsFromBackend = async () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  try {
    const res = await fetch(`${API_URL}/api/progress`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) return null;
    const progress = await res.json();
    return {
      stats: progress.stats || null,
      claimed_challenges: progress.claimed_challenges || null
    };
  } catch {
    return null;
  }
};

export function useChallenges() {
  // Load stats from localStorage
  const [stats, setStats] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_stats');
    return saved ? { ...INITIAL_STATS, ...JSON.parse(saved) } : INITIAL_STATS;
  });
  
  // Load claimed challenges from localStorage
  const [claimedChallenges, setClaimedChallenges] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_claimedChallenges');
    return saved ? JSON.parse(saved) : [];
  });

  // On mount: load from backend if localStorage is empty or stale
  useEffect(() => {
    const syncFromBackend = async () => {
      const data = await loadStatsFromBackend();
      if (!data) return;

      // Merge: take the max of each stat between local and backend
      if (data.stats && typeof data.stats === 'object') {
        setStats(prev => {
          const merged = { ...INITIAL_STATS };
          const allKeys = new Set([...Object.keys(prev), ...Object.keys(data.stats)]);
          for (const key of allKeys) {
            const local = prev[key] || 0;
            const backend = data.stats[key] || 0;
            merged[key] = typeof local === 'number' ? Math.max(local, backend) : (local || backend);
          }
          localStorage.setItem('fruitCrush_stats', JSON.stringify(merged));
          return merged;
        });
      }

      if (data.claimed_challenges && Array.isArray(data.claimed_challenges) && data.claimed_challenges.length > 0) {
        setClaimedChallenges(prev => {
          const merged = [...new Set([...prev, ...data.claimed_challenges])];
          localStorage.setItem('fruitCrush_claimedChallenges', JSON.stringify(merged));
          return merged;
        });
      }
    };
    syncFromBackend();
  }, []);

  // Save stats to localStorage and sync
  useEffect(() => {
    localStorage.setItem('fruitCrush_stats', JSON.stringify(stats));
    if (isSyncEnabled()) {
      syncStatsToBackend(stats, claimedChallenges);
    }
  }, [stats, claimedChallenges]);
  
  // Save claimed challenges to localStorage
  useEffect(() => {
    localStorage.setItem('fruitCrush_claimedChallenges', JSON.stringify(claimedChallenges));
  }, [claimedChallenges]);

  // Add eliminated fruits to stats
  const addEliminatedFruits = useCallback((fruitCounts) => {
    setStats(prev => {
      const newStats = { ...prev };
      let totalAdded = 0;
      
      Object.entries(fruitCounts).forEach(([fruit, count]) => {
        if (fruit in newStats && fruit !== 'total_fruits') {
          newStats[fruit] += count;
          totalAdded += count;
        }
      });
      
      newStats.total_fruits += totalAdded;
      return newStats;
    });
  }, []);
  
  // Add disco combo
  const addDiscoCombos = useCallback((count = 1) => {
    setStats(prev => ({
      ...prev,
      disco_combos: prev.disco_combos + count,
    }));
  }, []);

  // Add striped fruit created
  const addStripedFruits = useCallback((count = 1) => {
    setStats(prev => ({
      ...prev,
      striped_fruits: prev.striped_fruits + count,
    }));
  }, []);

  // Add wrapped fruit created
  const addWrappedFruits = useCallback((count = 1) => {
    setStats(prev => ({
      ...prev,
      wrapped_fruits: prev.wrapped_fruits + count,
    }));
  }, []);

  // Add special combo triggered
  const addSpecialCombos = useCallback((count = 1) => {
    setStats(prev => ({
      ...prev,
      special_combos: prev.special_combos + count,
    }));
  }, []);

  // Add level completed
  const addLevelCompleted = useCallback((isPerfect = false) => {
    setStats(prev => ({
      ...prev,
      levels_completed: prev.levels_completed + 1,
      perfect_wins: isPerfect ? prev.perfect_wins + 1 : prev.perfect_wins,
    }));
  }, []);

  // Add XP earned (for total XP tracking)
  const addTotalXP = useCallback((amount) => {
    setStats(prev => ({
      ...prev,
      total_xp: prev.total_xp + amount,
    }));
  }, []);

  // Get challenge progress
  const getChallengeProgress = useCallback((challenge, prestige = 0) => {
    let current = 0;
    
    if (challenge.type === 'prestige') {
      current = prestige;
    } else if (challenge.type === 'total_fruits') {
      current = stats.total_fruits;
    } else if (challenge.type === 'disco_combos') {
      current = stats.disco_combos;
    } else if (stats[challenge.type] !== undefined) {
      current = stats[challenge.type];
    }
    
    const progress = Math.min((current / challenge.target) * 100, 100);
    const isComplete = current >= challenge.target;
    const isClaimed = claimedChallenges.includes(challenge.id);
    
    return {
      current,
      target: challenge.target,
      progress,
      isComplete,
      isClaimed,
      canClaim: isComplete && !isClaimed,
    };
  }, [stats, claimedChallenges]);

  // Claim a challenge reward
  const claimChallenge = useCallback((challengeId, addXP) => {
    console.log('[CLAIM] Attempting to claim challenge:', challengeId);
    
    // Find the challenge
    const allChallenges = [...CHALLENGES.regular, ...CHALLENGES.master];
    const challenge = allChallenges.find(c => c.id === challengeId);
    
    if (!challenge) {
      console.log('[CLAIM] Challenge not found:', challengeId);
      return false;
    }
    
    // Check if already claimed
    if (claimedChallenges.includes(challengeId)) {
      console.log('[CLAIM] Already claimed:', challengeId, 'Claimed list:', claimedChallenges);
      return false;
    }
    
    console.log('[CLAIM] Claiming challenge:', challengeId, 'Reward:', challenge.reward);
    
    // Add XP reward
    if (addXP) {
      addXP(challenge.reward);
      console.log('[CLAIM] Added XP:', challenge.reward);
    }
    
    // Mark as claimed
    setClaimedChallenges(prev => {
      const newClaimed = [...prev, challengeId];
      console.log('[CLAIM] Updated claimed list:', newClaimed);
      return newClaimed;
    });
    
    return true;
  }, [claimedChallenges]);

  // Get all challenges with their status
  const getAllChallengesWithStatus = useCallback((prestige = 0) => {
    const regular = CHALLENGES.regular.map(challenge => ({
      ...challenge,
      ...getChallengeProgress(challenge, prestige),
    }));
    
    const master = CHALLENGES.master.map(challenge => ({
      ...challenge,
      ...getChallengeProgress(challenge, prestige),
    }));
    
    return { regular, master };
  }, [getChallengeProgress]);

  // Get count of completed/claimable challenges
  const getChallengesCounts = useCallback((prestige = 0) => {
    const { regular, master } = getAllChallengesWithStatus(prestige);
    const all = [...regular, ...master];
    
    return {
      total: all.length,
      completed: all.filter(c => c.isComplete).length,
      claimed: all.filter(c => c.isClaimed).length,
      canClaim: all.filter(c => c.canClaim).length,
    };
  }, [getAllChallengesWithStatus]);

  // Reset stats (for testing)
  const resetStats = useCallback(() => {
    setStats(INITIAL_STATS);
    setClaimedChallenges([]);
    localStorage.setItem('fruitCrush_stats', JSON.stringify(INITIAL_STATS));
    localStorage.setItem('fruitCrush_claimedChallenges', JSON.stringify([]));
  }, []);

  // Get all fruit levels based on current stats
  const getFruitLevels = useCallback(() => {
    return ALL_FRUIT_TYPES.map(fruit => {
      const eliminations = stats[fruit.id] || 0;
      const progress = getFruitLevelProgress(eliminations);
      return {
        ...fruit,
        eliminations,
        ...progress,
        // Add XP (using eliminations * 10 as XP value)
        xp: eliminations * 10,
        // Add matches count (same as eliminations for display)
        matches: eliminations,
        // Add combos (estimate based on level)
        combos: Math.floor(eliminations / 10),
        // Progress percent for the bar
        progressPercent: progress.percentage || 0,
      };
    });
  }, [stats]);

  // Get fruit mastery bonus for a specific fruit type
  // Returns bonus score points (level 10 = +5, level 20 = +10, etc.)
  const getFruitMasteryBonus = useCallback((fruitType) => {
    const eliminations = stats[fruitType] || 0;
    const progress = getFruitLevelProgress(eliminations);
    const level = progress.level || 1;
    // Every 10 levels gives +5 score bonus
    return Math.floor(level / 10) * 5;
  }, [stats]);

  // Get total mastery bonus for a set of matched fruits
  const calculateMasteryBonus = useCallback((matchedFruits) => {
    let totalBonus = 0;
    for (const fruitType of matchedFruits) {
      totalBonus += getFruitMasteryBonus(fruitType);
    }
    return totalBonus;
  }, [getFruitMasteryBonus]);

  return {
    stats,
    claimedChallenges,
    addEliminatedFruits,
    addDiscoCombos,
    addStripedFruits,
    addWrappedFruits,
    addSpecialCombos,
    addLevelCompleted,
    addTotalXP,
    getChallengeProgress,
    claimChallenge,
    getAllChallengesWithStatus,
    getChallengesCounts,
    resetStats,
    getFruitLevels,
    getFruitMasteryBonus,
    calculateMasteryBonus,
  };
}
